import useTranslation from "next-translate/useTranslation"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { Button, Spinner } from "reactstrap"

import { useTooltip } from "@services/tooltipHook"

import Icon, { IconName } from "../Icon"

/**
 * Used to decide, what to display on a form button: only icons or titles or both
 */
export const enum DisplayModeFormButtons {
  IconOnly = "iconsOnly",
  TitleOnly = "titlesOnly",
  IconAndTitle = "iconsAndTitles"
}

/**
 * a button may be a submit button with submit role or a simple button
 */
export enum ButtonRole {
  Default = "button",
  Submit = "submit",
  Reset = "reset"
}


/**
 * attributes for every button
 */
export interface ButtonProps {
  /**
   * title of the button
   * also represents the button label/button text
   */
  title?: string
  disabled?: boolean
  /**
   * a button may be displayed with icon and/or text
   */
  displayMode?: DisplayModeFormButtons
  /**
   * icon of a button
   */
  iconName?: IconName
  /**
   * function to be called on click
   */
  onClick?: (...args: any) => void
  /**
   * If a button acts with role "submit", it submits the form. If its role is Default ("button") onClick should be set.
   */
  role?: ButtonRole
}

interface IProps {
  isSubmitting: boolean
  removeButton?: ButtonProps
  submitButton?: ButtonProps
  cancelButton?: ButtonProps
  resetButton?: ButtonProps
  className?: string
  iconSize?: number
}

/**
 * This component provides a standardized appearance and structure for following buttons in a form:
 * remove button, submit button, cancel button
 *
 * It is used in any kind of submission from a user, e.g. activating a proposal, submit a proposal,
 * submitting a form (within a modal or not within a modal). The 'form' can have zero input values (submit a proposal) or several
 * input values (e.g. registration form).
 *
 * Every button has a default content & behaviour, which is used if an empty button prop is provided:
 * by default every button has text + icon.
 * The coder may change single attributes of every button.
 *
 * e.g. the display mode:
 * IconsOnly: Showing just the icons of buttons (e.g. change aboutMe in user profile), not the text
 * TitlesOnly: Showing just the title of the buttons (e.g. submit a proposal), not the icon
 * IconsAndTitles (default): Showing icons and the title of the button (forms within a modal and not within a modal, e.g. project profile)
 *
 *
 * NOTE: If a remove button and a submit button is active, the components helps, that the spinner appears just at the clicked button.
 * The remove button role is "button" by default, not submit. If the coder wants the remove button to be a submit button he has to define this explicitely. (example: ImageUploadForm)
 */
export const FormButtonArea = ({
  isSubmitting = false,
  removeButton,
  submitButton,
  cancelButton,
  resetButton,
  className,
  iconSize = 25,
}: IProps): JSX.Element => {

  const router = useRouter()


  const defaultRemoveButton: IFormButtonProps = {
    title: "form.removeElement",
    displayMode: DisplayModeFormButtons.IconAndTitle,
    role: ButtonRole.Default,
    className: "btn",
    buttonLabel: "form.removeElement",
    iconName: "trash",
    iconSize,
    isSubmitting, // isSubmitting is a component prop and is set as button specific default
    showSpinnerOnClick: true
  }

  const defaultSubmitButton: IFormButtonProps = {
    title: "form.save",
    displayMode: DisplayModeFormButtons.IconAndTitle,
    role: ButtonRole.Submit,
    className: "primary-btn",
    buttonLabel: "form.save",
    iconName: "save",
    iconSize,
    isSubmitting, // isSubmitting is a component prop and is set as button specific default
    showSpinnerOnClick: true
  }

  const defaultCancelButton: IFormButtonProps = {
    title: "form.cancel",
    displayMode: DisplayModeFormButtons.IconAndTitle,
    role: ButtonRole.Default,
    className: "btn",
    buttonLabel: "form.cancel",
    iconName: "cancel",
    iconSize,
    isSubmitting, // isSubmitting is a component prop and is set as button specific default
    onClick: router.back,
    showSpinnerOnClick: false
  }

  const defaultResetButton: IFormButtonProps = {
    title: "form.reset",
    displayMode: DisplayModeFormButtons.IconAndTitle,
    role: ButtonRole.Reset,
    className: "btn",
    buttonLabel: "form.reset",
    iconName: "revert",
    iconSize,
    isSubmitting, // isSubmitting is a component prop and is set as button specific default
    showSpinnerOnClick: false
  }


  return <div className={className ? className : "btn-flex-layout"}>
    {FormButton(defaultRemoveButton, removeButton)}
    {FormButton(defaultSubmitButton, submitButton)}
    {FormButton(defaultCancelButton, cancelButton)}
    {FormButton(defaultResetButton, resetButton)}
  </div>
}



/*
* don't extract the following component into an extra file, because it is only used within this component
*/
interface IFormButtonProps extends ButtonProps {
  className: string
  onClick?: (...args: any) => void
  role?: ButtonRole
  disabled?: boolean
  isSubmitting: boolean
  iconName?: IconName
  iconSize?: number
  title?: string
  buttonLabel?: string
  showSpinnerOnClick?: boolean
}

const FormButton = (defaultButtonProps: IFormButtonProps, incomingButtonProps: ButtonProps): JSX.Element => {
  const { t } = useTranslation("common")
  // remember if the button was clicked in order to show the spinner only at the clicked button
  const [buttonWasClicked, setButtonWasClicked] = useState(false)

  useEffect(() => {
    setButtonWasClicked(false)
  }, [])

  // merge incoming props over default values
  const realButton: IFormButtonProps = {
    ...defaultButtonProps,
    ...incomingButtonProps,
    // buttonLabel is not included in ButtonProps: use incoming title or default buttonLabel
    buttonLabel: incomingButtonProps?.title || defaultButtonProps.buttonLabel,
  }

  const { tooltipTargetRef, ToolTip } = useTooltip(t(realButton.title))

  return incomingButtonProps && <><Button
    title={t(realButton.title)}
    className={realButton.className + (realButton.displayMode === DisplayModeFormButtons.IconOnly ? " icon-btn" : "")}
    disabled={realButton.disabled || realButton.isSubmitting}
    onClick={() => {
      if (realButton.onClick) {
        realButton.onClick()
      }
      setButtonWasClicked(true)
    }}
    type={realButton.role}
    // every button should be findable by role (e.g. screen.getByRole("submit"))
    // in tests
    role={realButton.role}
    innerRef={tooltipTargetRef}
  >
    {realButton.displayMode !== DisplayModeFormButtons.TitleOnly && realButton.iconName && <Icon name={realButton.iconName} size={realButton.iconSize} />}
    {realButton.displayMode !== DisplayModeFormButtons.IconOnly && t(realButton.buttonLabel)}
    {realButton.showSpinnerOnClick && realButton.isSubmitting && buttonWasClicked && <Spinner />}
  </Button>

    {realButton.displayMode === DisplayModeFormButtons.IconOnly && ToolTip}
  </>
}